import React, { useState, useEffect, useMemo } from "react";

/* ───────────── helpers ───────────── */

const getJobCraftsmanName = (job) => {
  if (!job) return "—";
  const c = job.craftsman;
  if (c && typeof c === "object") {
    const name = c.full_name || c.name || c.user?.full_name || c.user?.username || null;
    if (name) return name;
  }
  return job.craftsman_name || job.craftsman_full_name || "—";
};

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// What the client actually paid. Matches the tracker and the M-Pesa screen:
// the approved quote if there is one, otherwise the budget.
const amountOf = (job) => {
  const total =
    positive(job.amount_paid) || positive(job.amount_due) || positive(job.budget) || 0;
  const fee = positive(job.platform_fee) || Math.round(total * 0.1);
  return { total, fee, service: Math.max(total - fee, 0) };
};

const paidDate = (job) => job.paid_at || job.updated_at || job.schedule || job.created_at || null;

const invoiceNo = (job) => `KZ-INV-${String(job.id).padStart(5, "0")}`;
const kes = (n) => `KSh ${Number(n || 0).toLocaleString("en-KE")}`;
const fmtDate = (v, long = false) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-KE", { day: "numeric", month: long ? "long" : "short", year: "numeric" });
};

/* ───────────── styles ───────────── */

const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #invoice-print-area, #invoice-print-area * { visibility: visible; }
  #invoice-print-area { position: fixed; inset: 0; margin: auto; max-width: 480px; box-shadow: none !important; border: none !important; max-height: none !important; }
  .no-print { display: none !important; }
}
`;

const INV_CSS = `
.inv { --ink:#0c0c0b; --muted:#6b6b66; --line:#e6e6e1; --fill:#f3f3ef; --y:#fff000;
  max-width: 1000px; margin: 0 auto; color: var(--ink); font-family: inherit; font-size: 14px; line-height: 1.5; }
.inv *, .inv *::before, .inv *::after { box-sizing: border-box; }
.inv :focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }

.inv-strip { display: grid; grid-template-columns: repeat(2, 1fr); margin-bottom: 16px; border: 1px solid var(--line); border-radius: 14px; background: #fff; }
.inv-stat { padding: 16px 22px; }
.inv-stat + .inv-stat { border-left: 1px solid var(--line); }
.inv-stat span { display: block; color: var(--muted); font-size: 13px; }
.inv-stat b { display: block; margin-top: 2px; font-size: 22px; font-weight: 700; letter-spacing: -.02em; font-variant-numeric: tabular-nums; }

.inv-card { background: #fff; border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.inv-wrap { overflow-x: auto; }
.inv-tbl { width: 100%; border-collapse: collapse; min-width: 640px; }
.inv-tbl th { padding: 12px 20px; background: #f6f6f3; color: var(--muted); font-size: 13px; font-weight: 500; text-align: left; white-space: nowrap; border-bottom: 1px solid var(--line); }
.inv-tbl td { padding: 14px 20px; border-bottom: 1px solid var(--line); vertical-align: middle; }
.inv-tbl tr:last-child td { border-bottom: 0; }
.inv-tbl tbody tr:hover { background: #fafaf8; }
.inv-num { font-weight: 600; }
.inv-amt { font-weight: 700; font-variant-numeric: tabular-nums; }
.inv-muted { color: var(--muted); }
.inv-view { min-height: 32px; padding: 0 14px; border: 1px solid var(--line); border-radius: 8px; background: #fff; color: var(--ink); font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.inv-view:hover { background: var(--fill); }

.inv-empty { padding: 56px 20px; text-align: center; background: #fff; border: 1px solid var(--line); border-radius: 14px; }
.inv-empty-ico { display: grid; place-items: center; width: 56px; height: 56px; margin: 0 auto 14px; border: 1px solid var(--line); border-radius: 50%; background: var(--fill); font-size: 1.15rem; }
.inv-empty h3 { margin: 0 0 4px; font-size: 15px; font-weight: 600; }
.inv-empty p { max-width: 340px; margin: 0 auto; color: var(--muted); }

.inv-bg { position: fixed; inset: 0; z-index: 800; display: flex; align-items: center; justify-content: center; padding: 20px; background: rgba(12,12,11,.55); }
.inv-box { width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; background: #fff; border: 1px solid var(--line); border-radius: 14px; box-shadow: 0 24px 80px rgba(0,0,0,.2); }
.inv-hd { position: relative; padding: 20px 24px; border-bottom: 1px solid var(--line); }
.inv-hd h2 { margin: 0; padding-right: 36px; font-size: 17px; font-weight: 700; letter-spacing: -.01em; }
.inv-hd p { margin: 3px 0 0; color: var(--muted); font-size: 13px; }
.inv-close { position: absolute; top: 14px; right: 16px; display: grid; place-items: center; width: 32px; height: 32px; border: 0; border-radius: 50%; background: var(--fill); color: var(--ink); font-size: 14px; cursor: pointer; }
.inv-close:hover { background: #e9e9e4; }
.inv-paid { display: inline-flex; align-items: center; gap: 6px; margin-top: 10px; padding: 3px 10px; border-radius: 6px; background: #e3f4e8; color: #17602f; font-size: 12px; font-weight: 600; }
.inv-bd { padding: 20px 24px; }
.inv-meta { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.inv-label { margin: 0 0 2px; color: var(--muted); font-size: 12px; font-weight: 500; }
.inv-val { margin: 0; font-weight: 600; }
.inv-hr { margin: 16px 0; border: 0; border-top: 1px solid var(--line); }
.inv-line { display: flex; justify-content: space-between; gap: 12px; padding: 5px 0; font-variant-numeric: tabular-nums; }
.inv-line.muted { color: var(--muted); }
.inv-total { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-top: 6px; padding: 14px 16px; border-radius: 10px; background: #fffbc7; font-weight: 600; }
.inv-total b { font-size: 22px; font-weight: 700; letter-spacing: -.02em; }
.inv-note { margin: 14px 0 0; color: var(--muted); font-size: 12px; }
.inv-ft { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 10px; padding: 16px 24px; border-top: 1px solid var(--line); }
.inv-btn { min-height: 40px; padding: 0 18px; border: 1px solid var(--line); border-radius: 8px; background: #fff; color: var(--ink); font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
.inv-btn:hover { background: var(--fill); }
.inv-btn.primary { border-color: var(--y); background: var(--y); color: #000; font-weight: 700; }
.inv-btn.primary:hover { background: #ffe600; border-color: #ffe600; }

@media (max-width: 600px) {
  .inv-bg { padding: 12px; }
  .inv-ft .inv-btn { flex: 1 1 100%; }
  .inv-strip { grid-template-columns: 1fr; }
  .inv-stat + .inv-stat { border-left: 0; border-top: 1px solid var(--line); }
}
`;

/* ───────────── component ───────────── */

export default function InvoicesTab({ jobs = [], clientName }) {
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    const id = "kaakazini-invoice-print-css";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.textContent = PRINT_CSS;
      document.head.appendChild(tag);
    }
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);

  useEffect(() => {
    if (!viewing) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setViewing(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewing]);

  const paid = useMemo(
    () =>
      (jobs || [])
        .filter((j) => /paid/i.test(j.status || ""))
        .sort((a, b) => new Date(paidDate(b) || 0) - new Date(paidDate(a) || 0)),
    [jobs]
  );

  const totalPaid = useMemo(() => paid.reduce((s, j) => s + amountOf(j).total, 0), [paid]);

  return (
    <div className="inv">
      <style>{INV_CSS}</style>

      {paid.length === 0 ? (
        <div className="inv-empty">
          <div className="inv-empty-ico"><i className="fas fa-file-invoice" aria-hidden="true" /></div>
          <h3>No invoices yet</h3>
          <p>Invoices for paid jobs will be listed here for your records.</p>
        </div>
      ) : (
        <>
          <div className="inv-strip">
            <div className="inv-stat"><span>Invoices</span><b>{paid.length}</b></div>
            <div className="inv-stat"><span>Total paid</span><b>{kes(totalPaid)}</b></div>
          </div>

          <div className="inv-card">
            <div className="inv-wrap">
              <table className="inv-tbl">
                <thead>
                  <tr>
                    <th>Invoice</th><th>Service</th><th>Craftsman</th><th>Paid on</th><th>Total</th><th />
                  </tr>
                </thead>
                <tbody>
                  {paid.map((job) => (
                    <tr key={job.id}>
                      <td className="inv-num">{invoiceNo(job)}</td>
                      <td>{job.service}</td>
                      <td className="inv-muted">{getJobCraftsmanName(job)}</td>
                      <td className="inv-muted">{fmtDate(paidDate(job))}</td>
                      <td className="inv-amt">{kes(amountOf(job).total)}</td>
                      <td style={{ textAlign: "right" }}>
                        <button type="button" className="inv-view" onClick={() => setViewing(job)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {viewing && (
        <InvoiceModal job={viewing} clientName={clientName} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

function InvoiceModal({ job, clientName, onClose }) {
  const { total, fee, service } = amountOf(job);

  return (
    <div className="inv-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="inv-box" id="invoice-print-area" role="dialog" aria-modal="true" aria-label={`Invoice ${invoiceNo(job)}`}>
        <div className="inv-hd">
          <button type="button" className="inv-close no-print" onClick={onClose} aria-label="Close invoice">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
          <h2>Invoice {invoiceNo(job)}</h2>
          <p>{job.service} · {getJobCraftsmanName(job)}</p>
          <span className="inv-paid"><i className="fas fa-check" aria-hidden="true" /> Paid</span>
        </div>

        <div className="inv-bd">
          <div className="inv-meta">
            <div>
              <p className="inv-label">Billed to</p>
              <p className="inv-val">{clientName || "Client"}</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <p className="inv-label">Paid on</p>
              <p className="inv-val">{fmtDate(paidDate(job), true)}</p>
            </div>
          </div>

          <hr className="inv-hr" />

          <div className="inv-line"><span>{job.service}</span><span>{kes(service)}</span></div>
          <div className="inv-line muted"><span>Platform fee</span><span>{kes(fee)}</span></div>

          <div className="inv-total">
            <span>Total paid</span>
            <b>{kes(total)}</b>
          </div>

          <p className="inv-note">Paid with M-Pesa. Keep this invoice for your records.</p>
        </div>

        <div className="inv-ft no-print">
          <button type="button" className="inv-btn" onClick={onClose}>Close</button>
          <button type="button" className="inv-btn primary" onClick={() => window.print()}>
            <i className="fas fa-print" aria-hidden="true" style={{ marginRight: 8 }} />
            Print / Save PDF
          </button>
        </div>
      </div>
    </div>
  );
}
